import { zodResolver } from '@hookform/resolvers/zod';
import CT from '@marketplace-ai/tags';
import { useEffect } from 'react';
import { FieldErrors, FormProvider, useWatch } from 'react-hook-form';
import {
  Button,
  DatePicker,
  FormActions,
  FormCell,
  FormLayout,
  FormRow,
  FormSection,
  Select,
  TextInput,
  Toggle,
} from '~/components';
import { STATES } from '~/constants';
import { useCustomForm } from '~/hooks/useCustomForm';
import { useServerWarnings } from '~/hooks/useServerWarnings';
import { logger } from '~/utils/logger';
import { CDS_LICENSE_TITLE_MAX, CdsLicenseFormSchema, CdsLicenseFormValues } from './types';

const CdsFormLogger = logger.child('CdsForm');
const TAGS = CT.account.licenses.cds.form;
const VALID_FIELDS: (keyof CdsLicenseFormValues)[] = [
  'issuingStateCode',
  'issuingAgency',
  'licenseTitle',
  'licenseNum',
  'expiryDate',
  'issueDate',
  'activeFlag',
];

interface Props {
  sendLicenseUpdate: (values: CdsLicenseFormValues) => void;
  sendLicenseCreate: (values: CdsLicenseFormValues) => void;
  onCancel: () => void;
  cdsLicenseForm: CdsLicenseFormValues;
  modal: {
    edit: boolean;
    create: boolean;
    type: 'dea' | 'cds';
  };
}

export const CdsLicenseForm = (props: Props) => {
  const { cdsLicenseForm, modal, sendLicenseCreate, sendLicenseUpdate, onCancel } = props;
  const methods = useCustomForm<CdsLicenseFormValues>({
    resolver: zodResolver(CdsLicenseFormSchema),
    defaultValues: cdsLicenseForm,
  });
  const { control, formState, trigger, handleSubmit } = methods;

  useServerWarnings(methods, cdsLicenseForm.warnings, VALID_FIELDS);

  const watchedExpiryDate = useWatch({ control, name: 'expiryDate' });
  const watchedActiveFlag = useWatch({ control, name: 'activeFlag' });
  useEffect(() => {
    if (formState.dirtyFields.expiryDate || formState.dirtyFields.activeFlag) {
      void trigger('activeFlag');
    }
  }, [
    formState.dirtyFields.activeFlag,
    formState.dirtyFields.expiryDate,
    trigger,
    watchedActiveFlag,
    watchedExpiryDate,
  ]);

  const onSubmitSuccess = async (values: CdsLicenseFormValues) => {
    if (modal.edit) {
      sendLicenseUpdate(values);
    } else {
      sendLicenseCreate(values);
    }
  };

  const onSubmitError = (errors: FieldErrors<CdsLicenseFormValues>) => {
    CdsFormLogger.error('Errors:', errors);
    CdsFormLogger.error('Current form values:', methods.getValues());
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmitSuccess, onSubmitError)}>
        <FormLayout>
          <FormSection componentsPerLine={1}>
            <FormRow>
              <Select
                name={'issuingStateCode'}
                label={'Issuing State*'}
                cyTag={TAGS.issuingStateCode}
                options={STATES}
              />
            </FormRow>
            <FormRow>
              <TextInput
                name={'issuingAgency'}
                cyTag={TAGS.issuingAgency}
                label={'Issuing Agency*'}
              />
            </FormRow>
          </FormSection>
          <FormSection componentsPerLine={2}>
            <FormRow>
              <TextInput
                name={'licenseTitle'}
                cyTag={TAGS.licenseTitle}
                label={'License Title'}
                maxLength={CDS_LICENSE_TITLE_MAX}
              />
            </FormRow>
            <FormRow>
              <TextInput name={'licenseNum'} cyTag={TAGS.licenseNumber} label={'License Number*'} />
            </FormRow>
          </FormSection>
          <FormSection componentsPerLine={2}>
            <FormRow>
              <DatePicker label={'Issue Date*'} cyTag={TAGS.issueDate} name="issueDate" />
            </FormRow>
            <FormRow>
              <DatePicker label={'Expiry Date*'} cyTag={TAGS.expiryDate} name={'expiryDate'} />
            </FormRow>
          </FormSection>
          <FormSection componentsPerLine={1}>
            <FormCell span={6} className="md:col-start-7">
              <FormRow>
                <Toggle<CdsLicenseFormValues>
                  cyTag={TAGS.activeFlag}
                  label={'Active Status'}
                  name={'activeFlag'}
                />
              </FormRow>
            </FormCell>
          </FormSection>
          <FormActions className="text-content-primary">
            <Button
              cyTag={TAGS.cancelBtn}
              type="button"
              label="Cancel"
              onClick={onCancel}
              colorVariant="secondary"
            />
            <Button
              cyTag={TAGS.submitBtn}
              type="submit"
              children={modal.edit ? <span>Update License </span> : <span>Add License</span>}
            />
          </FormActions>
        </FormLayout>
      </form>
    </FormProvider>
  );
};
